import React from 'react';
import type { ModelProviderVO } from '../services/aiAssistant';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

interface Props {
  value: string | null;
  providers: ModelProviderVO[];
  onChange: (modelId: string) => void;
  disabled?: boolean;
}

const TRIGGER_TEXT_PLACEHOLDER = '选择模型';

function resolveModelName(value: string | null, providers: ModelProviderVO[]): string {
  if (!value) return TRIGGER_TEXT_PLACEHOLDER;
  for (const p of providers) {
    for (const m of p.models || []) {
      if (m.modelId === value) return m.modelName;
    }
  }
  return value;
}

function findProviderValueByModelId(modelId: string | null, providers: ModelProviderVO[]): string | undefined {
  if (!modelId) return undefined;
  for (const p of providers) {
    if ((p.models || []).some((m) => m.modelId === modelId)) return p.value;
  }
  return undefined;
}

export default function ModelPicker({ value, providers, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popupPos, setPopupPos] = useState<{ left: number; bottom: number } | null>(null);
  const initialHover = useMemo(
    () => findProviderValueByModelId(value, providers) || providers[0]?.value || '',
    // 重新计算时机：弹层重新打开
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );
  const [hoverGroup, setHoverGroup] = useState<string>(initialHover);
  const [triggerHovered, setTriggerHovered] = useState(false);
  const [hoveredModelId, setHoveredModelId] = useState<string | null>(null);

  // 弹层打开时测量 trigger 位置（fixed 定位脱离父容器 overflow:hidden）
  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      setHoveredModelId(null);
      return;
    }
    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPopupPos({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 6
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  // 弹层每次打开重置 hover 到当前模型所属供应商
  const handleToggle = () => {
    if (disabled) return;
    if (!open) {
      setHoverGroup(findProviderValueByModelId(value, providers) || providers[0]?.value || '');
    }
    setOpen((v) => !v);
  };

  const triggerLabel = resolveModelName(value, providers);
  const activeProvider = providers.find((p) => p.value === hoverGroup);

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        ref={triggerRef}
        onClick={handleToggle}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '5px 10px',
          borderRadius: 6,
          border: 'none',
          background: open ? '#EEF1F5' : triggerHovered && !disabled ? '#F5F5F5' : 'transparent',
          fontSize: 12,
          color: '#1d2129',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          maxWidth: 200
        }}
        onMouseEnter={() => setTriggerHovered(true)}
        onMouseLeave={() => setTriggerHovered(false)}
        title={triggerLabel}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: value ? '#1d2129' : '#8a94a6'
          }}
        >
          {triggerLabel}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
            color: '#8a94a6',
            flexShrink: 0
          }}
        >
          <path
            d="M2.8 4.4L6 7.6L9.2 4.4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {open && popupPos && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1102 }} />
          <div
            style={{
              position: 'fixed',
              left: popupPos.left,
              bottom: popupPos.bottom,
              zIndex: 1103,
              display: 'flex',
              background: '#fff',
              borderRadius: 10,
              border: '1px solid #EFEFEF',
              boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              minWidth: 340,
              maxHeight: 'min(420px, calc(100vh - 80px))'
            }}
          >
            <div
              style={{
                borderRight: '1px solid #F0F0F0',
                padding: '6px 0',
                minWidth: 130,
                height: 360,
                overflowY: 'auto'
              }}
            >
              {providers.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onMouseEnter={() => setHoverGroup(g.value)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '8px 14px',
                    fontSize: 13,
                    border: 'none',
                    background: hoverGroup === g.value ? '#F5F5F5' : 'transparent',
                    fontWeight: hoverGroup === g.value ? 600 : 400,
                    color: '#1d2129',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {g.label}
                  </span>
                  <span style={{ color: '#BFBFBF', fontSize: 12, flexShrink: 0, marginLeft: 8 }}>›</span>
                </button>
              ))}
            </div>
            <div style={{ padding: '6px 0', minWidth: 210, height: 360, overflowY: 'auto' }}>
              {(activeProvider?.models || []).map((m) => {
                const selected = m.modelId === value;
                const hovered = m.modelId === hoveredModelId;
                return (
                  <button
                    key={m.modelId}
                    type="button"
                    onClick={() => {
                      onChange(m.modelId);
                      setOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 14px',
                      fontSize: 13,
                      border: 'none',
                      color: selected ? '#1677ff' : '#1d2129',
                      background: selected ? '#E6F0FF' : hovered ? '#F5F5F5' : 'transparent',
                      fontWeight: selected ? 600 : 400,
                      cursor: 'pointer'
                    }}
                    onMouseEnter={() => setHoveredModelId(m.modelId)}
                    onMouseLeave={() => setHoveredModelId((id) => (id === m.modelId ? null : id))}
                  >
                    {m.modelName}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
